"use client";

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Pencil, Loader2 } from "lucide-react";
import { formatCurrency, formatDate } from "@/lib/utils";
import { Subscription, Customer, Vehicle, Appointment } from "@/types";
import { userSubscriptionService } from "@/services/userSubscriptionService";
import { UserSubscriptionFeature } from "@/types/userSubscription.types";

interface SubscriptionDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscription: Subscription | null;
  customers: Customer[];
  vehicles: Vehicle[];
  appointments: Appointment[];
  getStatusBadge: (status: string) => JSX.Element;
  onEdit?: (subscription: Subscription) => void;
}

export function SubscriptionDetailDialog({
  open,
  onOpenChange,
  subscription,
  customers,
  vehicles,
  appointments,
  getStatusBadge,
  onEdit,
}: SubscriptionDetailDialogProps) {
  // State for dynamic features
  const [features, setFeatures] = useState<UserSubscriptionFeature[]>([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);

  // Fetch features when dialog opens
  useEffect(() => {
    if (open && subscription?.id) {
      setLoadingFeatures(true);
      setFeatures([]);
      userSubscriptionService
        .getUserSubscriptionFeatures(subscription.id)
        .then((data) => {
          setFeatures(data);
        })
        .catch((error) => {
          console.error("Error fetching subscription features:", error);
        })
        .finally(() => {
          setLoadingFeatures(false);
        });
    }
  }, [open, subscription?.id]);

  if (!subscription) return null;

  // Use customer data from subscription if available and valid, otherwise lookup from array
  const backendCustomerName = subscription.customerName;
  const isValidCustomerName = backendCustomerName && 
    backendCustomerName.trim() !== "" && 
    backendCustomerName.toLowerCase() !== "unknown customer";
  
  const customer = isValidCustomerName && subscription.customerPhone
    ? {
        id: subscription.customerId,
        name: backendCustomerName!,
        phone: subscription.customerPhone,
      }
    : customers.find((c) => c.id === subscription.customerId);
  
  // Use vehicle data from subscription if available, otherwise lookup from array
  let vehicle = null;
  
  if (subscription.vehicleMake && subscription.vehicleModel) {
    // Use vehicle data directly from subscription
    vehicle = {
      id: subscription.vehicleId,
      make: subscription.vehicleMake,
      model: subscription.vehicleModel,
      plateNumber: subscription.vehiclePlateNumber || "",
    };
  } else if (subscription.vehicleId) {
    // Lookup vehicle by vehicleId
    vehicle = vehicles.find((v) => v.id === subscription.vehicleId);
  }
  
  // If still not found and we have customerId, try to find any vehicle for this customer
  if (!vehicle && subscription.customerId) {
    const customerVehicles = vehicles.filter((v) => v.customerId === subscription.customerId);
    if (customerVehicles.length > 0) {
      vehicle = customerVehicles[0];
    }
  }
  const subscriptionAppointments = appointments.filter(
    (apt) => apt.type === "USER_SUBSCRIPTION" && apt.typeId === subscription.id
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>Subscription Details</DialogTitle>
              <DialogDescription>#{subscription.id}</DialogDescription>
            </div>
            {onEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onOpenChange(false);
                  onEdit(subscription);
                }}
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        </DialogHeader>
        <div className="space-y-6">
          {/* Info */}
          <Card>
            <CardHeader>
              <CardTitle>Subscription Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Customer</Label>
                  <p>{customer?.name || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Vehicle</Label>
                  <p>
                    {vehicle
                      ? `${vehicle.make} ${vehicle.model}`
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Plan</Label>
                  <p>{subscription.planName}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Monthly Price</Label>
                  <p className="font-semibold">
                    {formatCurrency(subscription.monthlyPrice)}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Start Date</Label>
                  <p>{formatDate(subscription.startDate)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">{getStatusBadge(subscription.status)}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Service Usage Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Subscription Features</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Overall Remaining Services */}
                {/* <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div>
                    <Label className="text-muted-foreground">Remaining Services</Label>
                    <p className="text-2xl font-bold">
                      {subscription.remainingServices} / {subscription.totalServices}
                    </p>
                  </div>
                </div> */}

                {/* Dynamic Features from API */}
                <div className="grid grid-cols-1 gap-3">
                  {loadingFeatures ? (
                    <div className="flex items-center justify-center p-4 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Loading features...
                    </div>
                  ) : features.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      No features configured for this subscription
                    </p>
                  ) : (
                    features.map((feature) => {
                      const isBoolean = feature.type === "Boolean";
                      const isNumeric = typeof feature.value === "number" || 
                        (typeof feature.value === "string" && !isNaN(Number(feature.value)));
                      
                      return (
                        <div 
                          key={feature.id}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div>
                            <Label className="text-muted-foreground">{feature.name}</Label>
                            <p className="text-sm font-medium">
                              {isBoolean ? (
                                feature.value ? "Enabled" : "Disabled"
                              ) : isNumeric ? (
                                `Balance: ${feature.value}`
                              ) : (
                                String(feature.value)
                              )}
                            </p>
                          </div>
                          <Badge variant={isBoolean ? (feature.value ? "default" : "secondary") : "secondary"}>
                            {isBoolean ? (
                              feature.value ? "Active" : "Inactive"
                            ) : (
                              `${feature.value} remaining`
                            )}
                          </Badge>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Generated Appointments */}
          <Card>
            <CardHeader>
              <CardTitle>Generated Appointments</CardTitle>
            </CardHeader>
            <CardContent>
              {subscriptionAppointments.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No appointments generated
                </p>
              ) : (
                <div className="space-y-2">
                  {subscriptionAppointments.map((appointment) => (
                    <div
                      key={appointment.id}
                      className="flex items-center justify-between border rounded-lg p-3"
                    >
                      <div>
                        <p className="font-medium">{appointment.type.replace(/_/g, " ")}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(appointment.date)}
                        </p>
                      </div>
                      <Badge variant="outline">{appointment.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}

